import { z } from 'zod'
import type { CalculatorDefinition } from '@/types/calculator'
import { positiveMoney, positiveInteger } from '@/calculators/shared/schema'
import { calculatePaye } from './calculation'
import { content, faqs } from './content'
import type { PayeInputs, PayeResult } from './types'

const schema = z.object({
  grossPay: positiveMoney('Enter a gross pay amount'),
  grossPayPeriod: z.enum(['monthly', 'annual']),
  ageBand: z.enum(['under65', '65to74', '75plus']),
  pensionContribution: positiveMoney(),
  pensionPeriod: z.enum(['monthly', 'annual']),
  medicalSchemeMember: z.boolean(),
  medicalDependants: positiveInteger(),
})

const defaults: PayeInputs = {
  grossPay: 35_000,
  grossPayPeriod: 'monthly',
  ageBand: 'under65',
  pensionContribution: 0,
  pensionPeriod: 'monthly',
  medicalSchemeMember: false,
  medicalDependants: 0,
}

export const payeDefinition: CalculatorDefinition<PayeInputs, PayeResult> = {
  id: 'paye',
  slug: 'salary-calculator',
  category: 'salary',
  title: 'South Africa PAYE & Take-Home Salary Calculator',
  shortTitle: 'PAYE Salary',
  description:
    'Estimate your monthly take-home pay in South Africa: PAYE, UIF, retirement deductions, and medical tax credits.',
  inputs: [
    {
      name: 'grossPay',
      label: 'Gross pay',
      type: 'money',
      required: true,
      placeholder: 'e.g. 35 000',
      unit: 'R',
    },
    {
      name: 'grossPayPeriod',
      label: 'Gross pay period',
      type: 'radio',
      required: true,
      options: [
        { value: 'monthly', label: 'Monthly' },
        { value: 'annual', label: 'Annual' },
      ],
      defaultValue: 'monthly',
    },
    {
      name: 'ageBand',
      label: 'Age band',
      type: 'select',
      required: true,
      options: [
        { value: 'under65', label: 'Under 65' },
        { value: '65to74', label: '65 to 74' },
        { value: '75plus', label: '75 or older' },
      ],
      defaultValue: 'under65',
    },
    {
      name: 'pensionContribution',
      label: 'Retirement / pension contribution',
      type: 'money',
      required: false,
      placeholder: '0',
      unit: 'R',
      hint: 'Total retirement-fund contribution for the selected period',
    },
    {
      name: 'pensionPeriod',
      label: 'Contribution period',
      type: 'radio',
      required: true,
      options: [
        { value: 'monthly', label: 'Monthly' },
        { value: 'annual', label: 'Annual' },
      ],
      defaultValue: 'monthly',
    },
    {
      name: 'medicalSchemeMember',
      label: 'Member of a medical scheme?',
      type: 'checkbox',
      defaultValue: false,
      hint: 'Tick to apply the SARS medical scheme fees tax credit',
    },
    {
      name: 'medicalDependants',
      label: 'Medical aid dependants',
      type: 'number',
      required: false,
      min: 0,
      step: 1,
      hint: 'Number of dependants on the medical aid (main member counted separately). Ignored if you are not a member.',
    },
  ],
  defaults,
  schema,
  calculate: calculatePaye,
  resultSections: [
    {
      id: 'netMonthly',
      label: 'Estimated net take-home (monthly)',
      format: 'currency',
      value: (r: PayeResult) => r.netMonthly,
      highlighted: true,
    },
    {
      id: 'annualGross',
      label: 'Annual gross',
      format: 'currency',
      value: (r: PayeResult) => r.annualGross,
    },
    {
      id: 'annualPaye',
      label: 'Annual PAYE',
      format: 'currency',
      value: (r: PayeResult) => r.annualPaye,
    },
    {
      id: 'monthlyPaye',
      label: 'Monthly PAYE',
      format: 'currency',
      value: (r: PayeResult) => r.monthlyPaye,
    },
    {
      id: 'uifMonthly',
      label: 'UIF (monthly)',
      format: 'currency',
      value: (r: PayeResult) => r.uifMonthly,
    },
    {
      id: 'annualPensionDeduction',
      label: 'Retirement deduction',
      format: 'currency',
      value: (r: PayeResult) => r.annualPensionDeduction,
    },
    {
      id: 'taxableIncome',
      label: 'Taxable income',
      format: 'currency',
      value: (r: PayeResult) => r.taxableIncome,
    },
    {
      id: 'effectiveTaxRate',
      label: 'Effective tax rate',
      format: 'percent',
      value: (r: PayeResult) => r.effectiveTaxRate,
    },
  ],
  assumptions: [
    {
      name: 'SARS income tax tables for individuals 2026/2027',
      url: 'https://www.sars.gov.za/tax-rates/income-tax/rates-of-tax-for-individuals/',
      reviewedAt: '2026-09-18',
      effectiveFrom: '2026-03-01',
      effectiveTo: '2027-02-28',
      version: '2027',
      notes: 'Uses primary, secondary and tertiary rebates and medical tax credits for 2026/2027.',
    },
    {
      name: 'UIF contribution limits',
      url: 'https://www.labour.gov.za/uif',
      reviewedAt: '2026-09-18',
      effectiveFrom: '2026-03-01',
      effectiveTo: '2027-02-28',
      version: '2027',
      notes: 'Employee UIF is 1% of remuneration up to the monthly earnings ceiling.',
    },
  ],
  related: ['salary-increase-calculator', 'vat-calculator', 'bond-calculator'],
  seo: {
    title: 'Salary Calculator South Africa (PAYE)',
    description:
      'Estimate PAYE, UIF and take-home pay after tax with this salary calculator for South Africa, using the current SARS tax tables.',
    keywords: ['PAYE calculator', 'South Africa salary tax', 'take-home pay', 'UIF', 'tax 2027'],
  },
  content,
  faqs,
}
